import React from 'react';

/**
 * Ayurdaya (Longevity) Display Component
 * Shows longevity estimate based on Parasara calculations
 */
const AyurdayaDisplay = ({ data }) => {
  if (!data || Object.keys(data).length === 0) {
    return <div className="p-4 bg-gray-50 rounded">No Ayurdaya data available</div>;
  }

  const estimatedYears = data.estimated_years || 0;
  const category = data.category || '';
  const range = data.range || '';
  const note = data.note || '';
  const methodStrengths = data.method_strengths || {};

  const getCategoryColor = () => {
    if (category.includes('Pūrṇāyu') || category.includes('Long')) {
      return 'from-green-500 to-emerald-600';
    } else if (category.includes('Madhyāyu') || category.includes('Middle')) {
      return 'from-blue-500 to-cyan-600';
    } else {
      return 'from-orange-500 to-red-600';
    }
  };

  const getCategoryIcon = () => {
    if (category.includes('Pūrṇāyu') || category.includes('Long')) {
      return '🌳';
    } else if (category.includes('Madhyāyu') || category.includes('Middle')) {
      return '🌿';
    } else {
      return '🌱';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border-2 border-purple-200">
        <h2 className="text-2xl font-bold text-purple-900 mb-2">
          ⏳ Ayurdaya (Longevity Estimate)
        </h2>
        <p className="text-sm text-gray-700">
          Traditional Vedic longevity calculation based on 1st lord, 8th lord, and Saturn's strength.
        </p>
      </div>

      {/* Main Estimate Card */}
      <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-lg">
        <div className={`p-8 bg-gradient-to-r ${getCategoryColor()}`}>
          <div className="text-center">
            <div className="text-6xl mb-4">{getCategoryIcon()}</div>
            <h3 className="text-4xl font-bold text-white mb-2">
              {estimatedYears} Years
            </h3>
            <p className="text-xl text-white text-opacity-90 mb-2">
              {category}
            </p>
            <p className="text-lg text-white text-opacity-80">
              Expected Range: {range}
            </p>
          </div>
        </div>

        {/* Method Strengths */}
        <div className="p-6 bg-gray-50">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">
            📊 Calculation Method Strengths
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">1st House Lord</span>
                <span className="text-xl font-bold text-purple-600">
                  {methodStrengths.first_lord?.toFixed(1) || '0.0'}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-500 h-2 rounded-full"
                  style={{ width: `${Math.min(methodStrengths.first_lord || 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-2">Represents self & vitality</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">8th House Lord</span>
                <span className="text-xl font-bold text-blue-600">
                  {methodStrengths.eighth_lord?.toFixed(1) || '0.0'}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{ width: `${Math.min(methodStrengths.eighth_lord || 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-2">Represents longevity directly</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Saturn (Āyuṣ Karaka)</span>
                <span className="text-xl font-bold text-indigo-600">
                  {methodStrengths.saturn?.toFixed(1) || '0.0'}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-500 h-2 rounded-full"
                  style={{ width: `${Math.min(methodStrengths.saturn || 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-2">Natural significator of longevity</p>
            </div>

            <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-4 rounded-lg border-2 border-purple-300">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-bold text-purple-900">Average Strength</span>
                <span className="text-2xl font-bold text-purple-700">
                  {methodStrengths.average?.toFixed(1) || '0.0'}%
                </span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full"
                  style={{ width: `${Math.min(methodStrengths.average || 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-purple-700 mt-2 font-semibold">Final calculation basis</p>
            </div>
          </div>
        </div>

        {/* Category Legend */}
        <div className="p-6 bg-white">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">
            📖 Longevity Categories (Parasara)
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🌳</span>
              <div>
                <p className="font-semibold text-green-700">Pūrṇāyu (Long Life)</p>
                <p className="text-gray-600">75-100 years • Strong planetary strength</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">🌿</span>
              <div>
                <p className="font-semibold text-blue-700">Madhyāyu (Middle Life)</p>
                <p className="text-gray-600">32-75 years • Moderate planetary strength</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">🌱</span>
              <div>
                <p className="font-semibold text-orange-700">Alpāyu (Short Life)</p>
                <p className="text-gray-600">0-32 years • Weak planetary strength</p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="p-6 bg-amber-50 border-t-2 border-amber-200">
          <div className="flex gap-3">
            <div className="text-2xl">⚠️</div>
            <div>
              <p className="font-semibold text-amber-900 mb-2">Important Note</p>
              <p className="text-sm text-amber-800">{note}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AyurdayaDisplay;
